import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { setAccountColorMap, setProductColorMap, clearColorMaps } from "../utils/trackListUtils";

/**
 * Context for managing account and product color maps.
 * Provides lifecycle management and prevents memory leaks by clearing state on unmount.
 * 
 * The color maps are also stored in a singleton (trackListUtils) for use by pure getter functions
 * that can't access React context (e.g., getAccountColor, getProductColor).
 * This hybrid approach ensures:
 * 1. React components get proper lifecycle management
 * 2. Pure functions can still access colors via the singleton
 * 3. Memory leaks are prevented through cleanup
 */
export const ColorMapContext = createContext(null);

/**
 * Hook to access color map context.
 * @throws {Error} If used outside ColorMapProvider
 */
export const useColorMapContext = () => {
  const context = useContext(ColorMapContext);
  if (!context) {
    throw new Error("useColorMapContext must be used within ColorMapProvider");
  }
  return context;
};

/**
 * Provider component for color map management.
 * Manages account and product color maps with proper lifecycle cleanup.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const ColorMapProvider = ({ children }) => {
  const [accountColorMap, setAccountColorMapState] = useState({});
  const [productColorMap, setProductColorMapState] = useState({});
  const [commodityColorMap, setCommodityColorMapState] = useState({});

  // Update account color map and sync with singleton
  const updateAccountColorMap = useCallback((map) => {
    setAccountColorMapState((prev) => {
      const next = { ...prev, ...map };
      // Update singleton for getter functions (getAccountColor, etc.)
      setAccountColorMap(map);
      return next;
    });
  }, []);

  // Update product color map and sync with singleton
  const updateProductColorMap = useCallback((map, products = []) => {
    setProductColorMapState((prev) => {
      const next = { ...prev, ...map };
      
      // Update commodity map
      setCommodityColorMapState((prevCommodity) => {
        const nextCommodityMap = { ...prevCommodity };
        if (products && products.length > 0) {
          products.forEach((p) => {
            if (p.reloadYardCommodityId && p.color) {
              if (!nextCommodityMap[p.reloadYardCommodityId]) {
                nextCommodityMap[p.reloadYardCommodityId] = p.color;
              }
            }
          });
        }
        return nextCommodityMap;
      });
      
      // Update singleton for getter functions (getProductColor, etc.)
      setProductColorMap(map, products);
      
      return next;
    });
  }, []);

  // Clear all color maps on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      setAccountColorMapState({});
      setProductColorMapState({});
      setCommodityColorMapState({});
      // Also clear singleton to prevent memory leaks
      clearColorMaps();
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  // Only recreate when the actual maps or setters change
  const value = useMemo(() => ({
    accountColorMap,
    productColorMap,
    commodityColorMap,
    setAccountColorMap: updateAccountColorMap,
    setProductColorMap: updateProductColorMap,
  }), [accountColorMap, productColorMap, commodityColorMap, updateAccountColorMap, updateProductColorMap]);

  return (
    <ColorMapContext.Provider value={value}>
      {children}
    </ColorMapContext.Provider>
  );
};

